import { Box, Typography } from "@mui/material";
import React from "react";

const INK = "#1A2A56", BODY = "#5b6472", ACCENT = "#5186EC", LINE = "#E7ECF4", CARD = "#F7F9FC", LABEL = "#8a93a3";

// Turn the LLM's inline markdown (**bold**, *italic*) into safe HTML so text renders
// cleanly instead of showing literal ** and * marks.
const escapeHtml = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const formatHtml = (text) => {
  let t = escapeHtml(text);
  t = t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>"); // bold first
  t = t.replace(/(^|[^*])\*(?!\s)([^*]+?)\*/g, "$1<em>$2</em>"); // then italics (single *)
  return t.trim();
};

// The dated "recent events" narrative gets rendered cleanly in Significant Events —
// cut it out of the Company Info blob so it isn't repeated as an inline wall of text.
const stripEventsTail = (text) => {
  if (!text) return "";
  return text
    .replace(/here are some recent events[\s\S]*$/i, "")
    .replace(/recent events and developments for the company[:.][\s\S]*$/i, "")
    .trim();
};

// Break one long blob into readable paragraphs at natural transition points.
const toParagraphs = (text) => {
  if (!text) return [];
  let t = text.replace(/\s+/g, " ").trim();
  const breaks = [
    "With an ARC Score",
    "Based on the available financial information",
    "Based on the available financial",
    "Following a senior underwriter review",
    "The recommended credit limit is capped",
  ];
  const SEP = " ";
  for (const b of breaks) t = t.split(b).join(SEP + b);
  return t.split(SEP).map((s) => s.trim()).filter(Boolean);
};

// Pull the key figures out of the prose for a scannable facts strip. Only facts we
// confidently match are shown; anything missing is simply omitted.
const extractFacts = (text) => {
  if (!text) return [];
  const out = [];
  const add = (label, value) => { if (value) out.push({ label, value }); };
  const g = (re) => { const m = text.match(re); return m ? m[1].replace(/\s+/g, " ").trim() : null; };

  add("Founded", g(/founded in (\d{4})/i));
  add("Revenue", g(/revenue of (?:approximately |about |around |~)?(\$?[\d.,]+\s*(?:billion|million|trillion|bn|mn))/i));
  add("Employees", g(/employs (?:approximately |about |around |~)?([\d,]+)/i));
  add("ARC Score", g(/ARC Score of ([\d.]+\s*\/\s*10)/i));
  add("Pulse Index", g(/Pulse Index of ([\d.]+\s*\/\s*10)/i));
  const risk = g(/considered to be at (?:a )?(\w+)[ -]risk/i) || g(/\bat (\w+) risk\b/i);
  add("Risk", risk ? risk.charAt(0).toUpperCase() + risk.slice(1).toLowerCase() : null);
  add("Recommended limit", g(/credit limit of (US\s?\$[\d,]+)/i) || g(/(\$[\d,]{5,}) is recommended/i));
  return out;
};

const SectionHeader = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Box sx={{ width: 4, height: 18, borderRadius: 2, background: ACCENT }} />
    <Typography sx={{ fontSize: 18, fontWeight: 700, color: INK }}>{children}</Typography>
  </Box>
);

const Para = ({ html }) => (
  <Typography component="p" sx={{ color: BODY, fontSize: 15.5, lineHeight: 1.75, mb: 1.75, maxWidth: 760 }}>
    <span dangerouslySetInnerHTML={{ __html: html }} />
  </Typography>
);

const FactsStrip = ({ facts }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mb: 3 }}>
    {facts.map((f) => (
      <Box key={f.label} sx={{ border: `1px solid ${LINE}`, borderRadius: "12px", px: 1.75, py: 1.1, background: CARD, minWidth: 118 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: LABEL }}>{f.label}</Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK, lineHeight: 1.3, mt: 0.25 }}>{f.value}</Typography>
      </Box>
    ))}
  </Box>
);

function CompanySummary({ company }) {
  const updatedSummary = company?.aiSummaryText
    ? company.aiSummaryText
        .replace(/\ba combined score\b/gi, "an ARC Score")
        .replace(/combined score/gi, "ARC Score")
        // Normalize section headers so the line-based parser still works when the model
        // emits them as markdown ("### 2. RECOMMENDATION") and/or inline with text.
        .replace(/\s*#{0,6}\s*(\d+\.\s*(?:COMPANY INFO|RECOMMENDATION|SIGNIFICANT EVENTS))\s*/gi, "\n$1\n")
    : "";

  const getSignificantEvents = (summary) => {
    try {
      if (!summary || typeof summary !== "string") return [];
      const noEventsMessages = [
        "No significant events were reported at this time.",
        "No significant events or news were reported at this time.",
        "No news available.",
        "No significant events found.",
      ];
      if (noEventsMessages.some((msg) => summary.includes(msg))) return [];

      let lines;
      try { lines = summary.split("\n"); } catch { return []; }

      const idx = lines.findIndex((line) => {
        try { return line.trim().toUpperCase().includes("3. SIGNIFICANT EVENTS"); } catch { return false; }
      });
      if (idx === -1) return [];

      const events = [];
      for (let i = idx + 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (["1.", "2.", "4."].some((p) => line.startsWith(p))) break;
        // Strip a leading bullet marker (- or *) only when followed by whitespace, so a
        // bold prefix like "**July 2026:**" is preserved. Also drop a leading "•".
        const cleaned = line.replace(/^\s*(?:[-*]\s+|•\s*)/, "").replace(/"$/, "");
        if (cleaned) events.push(cleaned);
      }
      return events;
    } catch { return []; }
  };

  const sliceSection = (summary, headerMatch, stopPrefixes) => {
    if (!summary) return "";
    const lines = summary.split("\n");
    const idx = lines.findIndex((line) => line.trim().toUpperCase().includes(headerMatch));
    if (idx === -1 || idx + 1 >= lines.length) return "";
    const acc = [];
    for (let i = idx + 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (stopPrefixes.some((p) => line.startsWith(p))) break;
      acc.push(line);
    }
    return acc.join(" ").trim();
  };

  const rawCompanyInfo = sliceSection(updatedSummary, "1. COMPANY INFO", ["1.", "2.", "3.", "4."]);
  const rawRecommendation = sliceSection(updatedSummary, "2. RECOMMENDATION", ["1.", "3.", "4."]);
  const significantEvents = getSignificantEvents(updatedSummary);

  const facts = extractFacts(rawCompanyInfo || updatedSummary);
  const infoParagraphs = toParagraphs(stripEventsTail(rawCompanyInfo));
  const recParagraphs = toParagraphs(rawRecommendation);

  if (!company?.aiSummaryText) {
    return (
      <Box padding="25px">
        <Typography variant="h6" sx={{ mb: 2 }}>Company Summary</Typography>
        <Typography>Company Summary Not Available!</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, md: 3.5 }, maxWidth: 960, ml: 0, mr: "auto" }}>
      {/* Company Info */}
      {(facts.length > 0 || infoParagraphs.length > 0) && (
        <Box sx={{ mb: 3.5 }}>
          <SectionHeader>Company Info</SectionHeader>
          {facts.length > 0 && <FactsStrip facts={facts} />}
          {infoParagraphs.map((p, i) => <Para key={i} html={formatHtml(p)} />)}
        </Box>
      )}

      {/* Recommendation */}
      {recParagraphs.length > 0 && (
        <Box sx={{ mb: 3.5, pt: 3, borderTop: `1px solid ${LINE}` }}>
          <SectionHeader>Recommendation</SectionHeader>
          {recParagraphs.map((p, i) => <Para key={i} html={formatHtml(p)} />)}
        </Box>
      )}

      {/* Significant Events */}
      {significantEvents.length > 0 && (
        <Box sx={{ pt: 3, borderTop: `1px solid ${LINE}` }}>
          <SectionHeader>Significant Events</SectionHeader>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 1.5 }}>
            {significantEvents.map((event, index) => (
              event?.trim() && (
                <Box component="li" key={index} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <Box sx={{ mt: "9px", width: 6, height: 6, borderRadius: "50%", background: ACCENT, flexShrink: 0 }} />
                  <Typography sx={{ color: BODY, fontSize: 15.5, lineHeight: 1.65 }}>
                    <span dangerouslySetInnerHTML={{ __html: formatHtml(event) }} />
                  </Typography>
                </Box>
              )
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default CompanySummary;
